import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Sparkles, Loader2, ChevronDown, ChevronUp, Check, RefreshCw, Scale, StickyNote } from "lucide-react";

const SECTION_LABELS = [
  ["injury_summary", "Injury Summary"], ["body_parts", "Body Parts Mentioned"], ["symptoms", "Symptoms Reported"],
  ["pain_levels", "Pain Levels Mentioned"], ["diagnoses", "Diagnoses"], ["medications", "Medications"],
  ["treatment_recommendations", "Treatment Recommendations"], ["follow_up_recommendations", "Follow-up Recommendations"],
  ["specialist_referrals", "Specialist Referrals Recommended"], ["imaging_ordered", "Imaging Ordered"],
  ["physical_therapy", "Physical Therapy Recommendations"], ["chiropractic", "Chiropractic Recommendations"],
  ["neurology_concussion_indicators", "Neurology / Concussion Indicators"], ["orthopedic_indicators", "Orthopedic Indicators"],
  ["pain_management_indicators", "Pain Management Indicators"], ["emergency_red_flags", "Emergency Red Flags"],
  ["restrictions", "Restrictions"], ["missed_work_references", "Missed Work References"],
  ["future_treatment_references", "Future Treatment References"], ["mmi_references", "MMI References"],
  ["permanent_injury_references", "Permanent Injury References"], ["prognosis", "Prognosis"],
];
const LEGAL_LABELS = [
  ["causation_statements", "Causation statements linking injuries to the accident"],
  ["liability_damages_support", "Statements supporting liability damages"],
  ["objective_findings", "Objective findings"], ["subjective_complaints", "Subjective complaints"],
  ["physician_observations", "Physician observations"], ["new_vs_preexisting", "New injuries vs pre-existing (as documented)"],
  ["recommended_future_care", "Recommended future care"],
  ["value_increasing_statements", "Statements potentially increasing case value"],
  ["missing_documentation", "Missing documentation to obtain"],
];

const openPdfAtPage = async (record, page) => {
  try {
    const res = await api.get(`/medical-records/${record.record_id}/file`, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    window.open(`${url}#page=${page || 1}`, "_blank");
  } catch { toast.error("Could not open the original PDF"); }
};

const Items = ({ record, value }) => {
  if (!value || (Array.isArray(value) && !value.length)) return <span className="text-slate-300">None documented</span>;
  if (typeof value === "string") return <span>{value}</span>;
  return (
    <ul className="space-y-0.5">
      {value.map((it, i) => {
        const text = typeof it === "string" ? it : it.text;
        const page = typeof it === "object" ? it.page : null;
        return (
          <li key={i} className="flex items-start gap-1.5">
            <span className="text-slate-300 mt-0.5">•</span>
            <span>{text}
              {page && <button onClick={() => openPdfAtPage(record, page)}
                className="ml-1.5 text-[10px] font-bold text-cobalt underline underline-offset-2" data-testid="ai-page-cite">p.{page}</button>}
            </span>
          </li>
        );
      })}
    </ul>
  );
};

export const AiSummaryPanel = ({ record }) => {
  const [data, setData] = useState(undefined);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [showLegal, setShowLegal] = useState(true);
  const load = useCallback(() => {
    api.get(`/ai/medical-summaries/record/${record.record_id}`).then(({ data: d }) => setData(d)).catch(() => setData(null));
  }, [record.record_id]);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setBusy(true);
    try { await api.post(`/ai/medical-summaries/record/${record.record_id}/generate`); toast.success("AI summary generated"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Generation failed"); }
    finally { setBusy(false); }
  };
  const review = async (flags = {}) => {
    try { await api.post(`/ai/medical-summaries/${data.summary.summary_id}/review`, flags); toast.success("Marked reviewed"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  const addNote = async () => {
    if (!note.trim()) return;
    try { await api.post(`/ai/medical-summaries/${data.summary.summary_id}/notes`, { note }); setNote(""); toast.success("Note added"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  const toggleCase = async () => {
    try {
      await api.put("/attorney/ai-settings", data.case_disabled ? { enable_case: record.case_id } : { disable_case: record.case_id });
      toast.success(data.case_disabled ? "AI summaries re-enabled for this case" : "AI summaries disabled for this case"); load();
    } catch { toast.error("Failed"); }
  };

  if (data === undefined) return <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 text-cobalt animate-spin" /></div>;
  if (data === null) return <div className="py-4 text-xs text-slate-400">Could not load AI intelligence.</div>;
  const s = data.summary;

  return (
    <div className="mt-3 rounded-2xl border border-indigo-100 bg-indigo-50/40 p-4" data-testid={`ai-summary-panel-${record.record_id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-navy"><Sparkles className="w-4 h-4 text-indigo-500" /> AI Medical Record Intelligence</div>
        <div className="flex flex-wrap gap-1.5">
          {s?.status === "ready" && !s.reviewed && (
            <button onClick={() => review()} data-testid="ai-mark-reviewed" className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-500 text-white text-xs font-semibold"><Check className="w-3 h-3" /> Mark reviewed</button>
          )}
          {s?.status === "ready" && s.reviewed && (
            <span className="text-[10px] font-bold uppercase px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-600" data-testid="ai-reviewed-badge">Reviewed by {s.reviewed.by_name}</span>
          )}
          <button onClick={generate} disabled={busy} data-testid="ai-regenerate" className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-slate-600 text-xs disabled:opacity-50">
            <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} /> {s ? "Regenerate" : "Generate"}
          </button>
          <button onClick={toggleCase} data-testid="ai-toggle-case" className="px-3 py-1.5 rounded-full bg-white border border-slate-200 text-slate-500 text-xs">
            {data.case_disabled ? "Enable for this case" : "Disable for this case"}
          </button>
        </div>
      </div>
      <p className="mt-2 text-[11px] font-semibold text-indigo-700 bg-indigo-100/70 rounded-xl px-3 py-2" data-testid="ai-disclaimer">{data.disclaimer}</p>

      {!s && <p className="mt-3 text-xs text-slate-500" data-testid="ai-none">{data.case_disabled ? "AI summaries are disabled for this case." : "No AI summary yet — it generates automatically on chart delivery, or click Generate."}</p>}
      {s?.status === "pending" && <p className="mt-3 text-xs text-slate-500 flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Analyzing chart…</p>}
      {s?.status === "failed" && <p className="mt-3 text-xs text-red-600" data-testid="ai-failed">Generation failed: {s.error}</p>}

      {s?.status === "ready" && (
        <div className="mt-3 space-y-3 text-xs text-slate-600">
          <div data-testid="ai-executive-summary">
            <div className="font-bold text-navy text-sm">Executive Summary</div>
            <p className="mt-1">{s.sections?.executive_summary || "—"}</p>
            <div className="text-[10px] text-slate-400 mt-1">v{s.version_analyzed} analyzed · {s.pages_analyzed} page(s) · {new Date(s.generated_at).toLocaleString()} · {s.model}</div>
          </div>
          <details data-testid="ai-clinical-sections">
            <summary className="cursor-pointer font-semibold text-navy flex items-center gap-1"><ChevronDown className="w-3.5 h-3.5" /> Clinical extraction (22 sections)</summary>
            <div className="mt-2 grid sm:grid-cols-2 gap-x-5 gap-y-3">
              {SECTION_LABELS.map(([k, label]) => (
                <div key={k}>
                  <div className="font-semibold text-slate-700">{label}</div>
                  <div className="mt-0.5"><Items record={record} value={s.sections?.[k]} /></div>
                </div>
              ))}
            </div>
          </details>
          <div className="rounded-xl bg-navy/[0.03] border border-slate-200 p-3" data-testid="ai-legal-intelligence">
            <button onClick={() => setShowLegal(!showLegal)} className="flex items-center gap-1.5 font-bold text-navy">
              <Scale className="w-3.5 h-3.5 text-cobalt" /> Legal Intelligence (attorney-only work product) {showLegal ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
            {showLegal && (
              <div className="mt-2 space-y-2.5">
                {LEGAL_LABELS.map(([k, label]) => (
                  <div key={k}>
                    <div className="font-semibold text-slate-700">{label}</div>
                    <div className="mt-0.5"><Items record={record} value={s.legal_intelligence?.[k]} /></div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => review({ follow_up_required: true })} data-testid="ai-flag-followup" className="px-3 py-1.5 rounded-full bg-amber-50 text-amber-600 text-xs font-semibold">Flag: Follow-up required</button>
            <button onClick={() => review({ records_complete: true })} data-testid="ai-flag-complete" className="px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-600 text-xs font-semibold">Flag: Records complete</button>
          </div>
          <div>
            <div className="font-semibold text-slate-700 flex items-center gap-1"><StickyNote className="w-3.5 h-3.5" /> Attorney notes</div>
            {(s.notes || []).map((n, i) => (
              <div key={i} className="mt-1 text-[11px] bg-white rounded-lg px-2.5 py-1.5 border border-slate-100">{n.note} <span className="text-slate-400">— {n.by}, {new Date(n.at).toLocaleString()}</span></div>
            ))}
            <div className="mt-1.5 flex gap-2">
              <input value={note} onChange={(e) => setNote(e.target.value)} data-testid="ai-note-input"
                className="flex-1 rounded-xl border border-slate-200 px-3 py-1.5 text-xs" placeholder="Add a private note…" />
              <button onClick={addNote} data-testid="ai-note-add" className="px-3 py-1.5 rounded-full bg-navy text-white text-xs font-semibold">Add</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const AiFirmToggle = () => {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/attorney/ai-settings").then(({ data }) => setS(data)).catch(() => {}); }, []);
  if (!s) return null;
  return (
    <button onClick={async () => {
      const { data } = await api.put("/attorney/ai-settings", { enabled: !s.enabled });
      setS(data); toast.success(data.enabled ? "AI summaries enabled firm-wide" : "AI summaries disabled firm-wide");
    }} data-testid="ai-firm-toggle"
      className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold ${s.enabled ? "bg-indigo-500 text-white" : "bg-slate-100 text-slate-500"}`}>
      <Sparkles className="w-3.5 h-3.5" /> AI summaries: {s.enabled ? "ON" : "OFF"} (firm-wide)
    </button>
  );
};
